/**
 * AnalysisView — full-screen waveform analysis page
 * Opened in a new tab via WaveformViewer "Expand" button.
 * State read from sessionStorage['pc_analysis_state'].
 * Three tabs: TWF | FFT | Waterfall (Three.js)
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as THREE from 'three';
import { X, Plus, GripVertical, ChevronLeft, ChevronRight } from 'lucide-react';
import { apiFetch } from '../api/client';
import { getToken } from '../api/client';

// ─── Types ───────────────────────────────────────────────────────────────────

type Signal = 'velocity' | 'accel' | 'envelope';
type TabType = 'twf' | 'fft' | 'waterfall';
type MarkerType = 'standalone' | 'harmonic' | 'sideband';

interface TWFGroup {
  measured_at: string;
  point_index: number;
  point_name: string;
  axes: Array<{ axis_id: number; axis_num: number; axis_label: string; file_id: number }>;
}
interface FFTTrace {
  file_id: number; axis_id: number; axis_num: number; axis_label: string;
  signal: string; unit: string; sample_rate: number; bin_count: number;
  freq_res_hz: number; freq_res_cpm: number; max_freq_cpm: number;
  amplitudes: number[]; peak_cpm: number; peak_amplitude: number;
  detected_rpm: number | null; measured_at: string; source: string;
}
interface TWFTrace {
  y: number[]; sample_rate: number; duration_sec: number;
  peak_pos: number; peak_neg: number; rms: number; crest_factor: number;
  source: string; signal_type: number;
}
interface AnalysisMarker {
  id: string; type: MarkerType; cpm: number; label: string; colour: string;
  fundamental?: number; count?: number; centre?: number; spacing?: number; sbCount?: number;
}
interface Panel {
  id: string; tabType: TabType; signal: Signal;
  heightPct: number; // 0-100 portion of available height
}
interface WaterfallSlice {
  slice_index: number; label: string; measured_at: string;
  file_id: number | null; twf_rms: number | null; days_ago: number;
  amplitudes: number[] | null;
  candidates: Array<{ file_id: number; measured_at: string; twf_rms: number; days_ago: number }>;
}
interface WaterfallData {
  axis_id: number; axis_label: string; point_name: string;
  signal: string; unit: string; freq_res_cpm: number; slices: WaterfallSlice[];
}

// ─── Constants ───────────────────────────────────────────────────────────────

const AXIS_COLORS: Record<string, string> = { H: '#2563eb', V: '#16a34a', A: '#06b6d4' };
const AXIS_NUM_COLORS: Record<number, string> = { 1: '#2563eb', 2: '#16a34a', 3: '#06b6d4' };
const SIGNALS: Signal[] = ['velocity', 'accel', 'envelope'];
function signalLabel(s: Signal) { return s === 'velocity' ? 'Vel' : s === 'accel' ? 'Acc' : 'Env'; }
function signalUnit(s: Signal)  { return s === 'velocity' ? 'in/s' : s === 'accel' ? 'G' : 'GE'; }
function uid() { return Math.random().toString(36).slice(2); }
function fmtEastern(ts: string): string {
  if (!ts) return '';
  const [datePart, timePart = '00:00:00'] = ts.split(' ');
  const sep = datePart.includes('/') ? '/' : '-';
  const [y, m, d] = datePart.split(sep).map(Number);
  const [hh, mm] = (timePart || '').split(':').map(Number);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const ampm = hh >= 12 ? 'PM' : 'AM';
  const h12 = hh % 12 || 12;
  return `${months[m-1]} ${d}, ${y} ${h12}:${String(mm).padStart(2,'0')} ${ampm}`;
}

// ─── FFT renderer (standalone) ────────────────────────────────────────────────

function renderFFTCanvas(
  canvas: HTMLCanvasElement,
  traces: FFTTrace[],
  xMaxCpm: number,
  markers: AnalysisMarker[],
  cursorCpm: number | null,
  operatingRpm: number | null,
  showRevMarkers: boolean,
) {
  const dpr = window.devicePixelRatio || 1;
  const W = canvas.offsetWidth, H = canvas.offsetHeight;
  if (!W || !H) return;
  canvas.width = W * dpr; canvas.height = H * dpr;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(dpr, dpr);
  const pad = { top: 30, right: 16, bottom: 46, left: 58 };
  const cW = W - pad.left - pad.right, cH = H - pad.top - pad.bottom;
  ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, W, H);
  let yMax = 0;
  for (const t of traces) {
    const maxBin = Math.min(t.bin_count - 1, Math.floor(xMaxCpm / t.freq_res_cpm));
    for (let i = 0; i <= maxBin; i++) if (t.amplitudes[i] > yMax) yMax = t.amplitudes[i];
  }
  yMax = yMax > 0 ? yMax * 1.15 : 1;
  ctx.strokeStyle = '#e5e7eb'; ctx.lineWidth = 0.5;
  for (let i = 0; i <= 5; i++) {
    const y = pad.top + cH * (1 - i / 5);
    ctx.beginPath(); ctx.moveTo(pad.left, y); ctx.lineTo(pad.left + cW, y); ctx.stroke();
  }
  ctx.fillStyle = '#6b7280'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'right';
  for (let i = 0; i <= 5; i++) {
    const val = yMax * i / 5;
    ctx.fillText(val.toFixed(4), pad.left - 4, pad.top + cH * (1 - i / 5) + 3);
  }
  ctx.save(); ctx.translate(12, pad.top + cH / 2); ctx.rotate(-Math.PI / 2);
  ctx.textAlign = 'center'; ctx.fillStyle = '#374151'; ctx.font = '11px Inter,sans-serif';
  ctx.fillText(traces[0]?.unit ?? '', 0, 0); ctx.restore();
  ctx.textAlign = 'center'; ctx.fillStyle = '#6b7280'; ctx.font = '10px Inter,sans-serif';
  for (let i = 0; i <= 6; i++) {
    const cpm = xMaxCpm * i / 6;
    ctx.fillText(cpm >= 1000 ? `${(cpm/1000).toFixed(0)}k` : cpm.toFixed(0), pad.left + cW * i / 6, pad.top + cH + 13);
  }
  ctx.fillStyle = '#374151'; ctx.font = '11px Inter,sans-serif';
  ctx.fillText('CPM', pad.left + cW / 2, pad.top + cH + 30);
  for (const t of traces) {
    const colour = AXIS_NUM_COLORS[t.axis_num] || '#94a3b8';
    const maxBin = Math.min(t.bin_count - 1, Math.floor(xMaxCpm / t.freq_res_cpm));
    ctx.strokeStyle = colour; ctx.lineWidth = 0.9; ctx.globalAlpha = 0.85; ctx.beginPath();
    let first = true;
    for (let i = 0; i <= maxBin; i++) {
      const x = pad.left + (i * t.freq_res_cpm / xMaxCpm) * cW;
      const y = pad.top + cH - (t.amplitudes[i] / yMax) * cH;
      if (first) { ctx.moveTo(x, y); first = false; } else ctx.lineTo(x, y);
    }
    ctx.stroke(); ctx.globalAlpha = 1;
  }
  if (showRevMarkers && operatingRpm && operatingRpm > 0) {
    ctx.setLineDash([4, 3]); ctx.lineWidth = 1;
    for (let h = 1; h <= 10; h++) {
      const hcpm = operatingRpm * h;
      if (hcpm > xMaxCpm) break;
      const x = pad.left + (hcpm / xMaxCpm) * cW;
      ctx.strokeStyle = '#fca5a5';
      ctx.beginPath(); ctx.moveTo(x, pad.top); ctx.lineTo(x, pad.top + cH); ctx.stroke();
      ctx.fillStyle = '#dc2626'; ctx.font = '9px Inter,sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(`h${h}`, x, pad.top + 10);
    }
    ctx.setLineDash([]);
  }
  const drawLine = (cpm: number, colour: string, dashed: boolean, label: string, yo = 0) => {
    if (cpm <= 0 || cpm > xMaxCpm) return;
    const x = pad.left + (cpm / xMaxCpm) * cW;
    ctx.strokeStyle = colour; ctx.lineWidth = 1;
    if (dashed) ctx.setLineDash([4, 3]); else ctx.setLineDash([]);
    ctx.beginPath(); ctx.moveTo(x, pad.top); ctx.lineTo(x, pad.top + cH); ctx.stroke();
    ctx.setLineDash([]);
    if (label) { ctx.fillStyle = colour; ctx.font = '9px Inter,sans-serif'; ctx.textAlign = 'center'; ctx.fillText(label, x, pad.top + 10 + yo); }
  };
  for (const m of markers) {
    if (m.type === 'standalone') drawLine(m.cpm, m.colour, false, m.label);
    else if (m.type === 'harmonic' && m.fundamental && m.count) {
      for (let h = 1; h <= m.count; h++) drawLine(m.fundamental * h, m.colour, true, `h${h}`);
    } else if (m.type === 'sideband' && m.centre && m.spacing && m.sbCount) {
      drawLine(m.centre, m.colour, false, m.label || 'SB');
      for (let s = 1; s <= m.sbCount; s++) {
        drawLine(m.centre + m.spacing * s, m.colour, true, `+${s}`);
        drawLine(m.centre - m.spacing * s, m.colour, true, `-${s}`);
      }
    }
  }
  if (cursorCpm !== null && cursorCpm >= 0 && cursorCpm <= xMaxCpm) {
    const cx = pad.left + (cursorCpm / xMaxCpm) * cW;
    ctx.strokeStyle = '#374151'; ctx.lineWidth = 1; ctx.setLineDash([4, 3]);
    ctx.beginPath(); ctx.moveTo(cx, pad.top); ctx.lineTo(cx, pad.top + cH); ctx.stroke();
    ctx.setLineDash([]);
  }
}

// ─── Three.js waterfall scene builder ────────────────────────────────────────

function makeTextSprite(text: string, color = '#374151', fontSize = 22, widthPx = 220, heightPx = 44): THREE.Sprite {
  const c = document.createElement('canvas');
  c.width = widthPx; c.height = heightPx;
  const ctx = c.getContext('2d')!;
  ctx.font = `${fontSize}px Inter,sans-serif`;
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, widthPx / 2, heightPx / 2);
  const mat = new THREE.SpriteMaterial({ map: new THREE.CanvasTexture(c), transparent: true });
  return new THREE.Sprite(mat);
}

function buildWaterfallScene(
  scene: THREE.Scene,
  slices: WaterfallSlice[],
  xMaxCpm: number,
  freqResCpm: number,
  waterfallMarkers: AnalysisMarker[],
  unit = '',
) {
  // Clear existing mesh objects (keep lights/helpers)
  const toRemove: THREE.Object3D[] = [];
  scene.traverse(obj => { if ((obj as any).__wfSlice || (obj as any).__wfMarker || (obj as any).__wfLabel || (obj as any).__wfGrid) toRemove.push(obj); });
  toRemove.forEach(obj => { scene.remove(obj); (obj as any).geometry?.dispose?.(); (obj as any).material?.dispose?.(); });

  const validSlices = slices.filter(s => s.amplitudes && s.amplitudes.length > 0);
  if (!validSlices.length) return;

  const nSlices = validSlices.length;
  let globalMax = 0;
  for (const s of validSlices) {
    const maxBin = Math.min(s.amplitudes!.length - 1, Math.floor(xMaxCpm / freqResCpm));
    for (let i = 0; i <= maxBin; i++) if (s.amplitudes![i] > globalMax) globalMax = s.amplitudes![i];
  }
  if (globalMax === 0) globalMax = 1;

  const getColor = (amp: number): [number, number, number] => {
    const t = Math.min(amp / globalMax, 1.0);
    return [(251 + (220 - 251) * t) / 255, (191 + (38 - 191) * t) / 255, (36 + (38 - 36) * t) / 255];
  };

  // Grid floor
  const gridGeo = new THREE.BufferGeometry();
  const gridVerts: number[] = [];
  // X grid lines
  for (let i = 0; i <= 6; i++) {
    const x = (i / 6) * 2 - 1;
    gridVerts.push(x, 0, 0, x, 0, -2);
  }
  // Z grid lines
  for (let i = 0; i <= nSlices - 1; i++) {
    const z = -i / Math.max(nSlices - 1, 1) * 2;
    gridVerts.push(-1, 0, z, 1, 0, z);
  }
  gridGeo.setAttribute('position', new THREE.Float32BufferAttribute(gridVerts, 3));
  const gridMat = new THREE.LineBasicMaterial({ color: 0xd1d5db });
  const gridLines = new THREE.LineSegments(gridGeo, gridMat);
  (gridLines as any).__wfGrid = true;
  scene.add(gridLines);

  for (let si = 0; si < validSlices.length; si++) {
    const slice = validSlices[si];
    const amps = slice.amplitudes!;
    const zPos = si === 0 ? 0 : -(si / (nSlices - 1)) * 2;
    const maxBin = Math.min(amps.length - 1, Math.floor(xMaxCpm / freqResCpm));

    const positions: number[] = [];
    const colors: number[] = [];
    const indices: number[] = [];

    for (let i = 0; i <= maxBin; i++) {
      const cpm = i * freqResCpm;
      const x = (cpm / xMaxCpm) * 2 - 1;
      const yTop = (amps[i] / globalMax) * 1.2;
      const [r, g, b] = getColor(amps[i]);
      positions.push(x, yTop, zPos);
      colors.push(r, g, b);
      positions.push(x, 0, zPos);
      colors.push(r * 0.6, g * 0.6, b * 0.6);
    }
    const n = maxBin + 1;
    for (let i = 0; i < n - 1; i++) {
      const tA = i * 2, bA = i * 2 + 1, tB = (i + 1) * 2, bB = (i + 1) * 2 + 1;
      indices.push(tA, bA, tB, bA, bB, tB);
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geo.setIndex(indices);
    const mat = new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide });
    const mesh = new THREE.Mesh(geo, mat);
    (mesh as any).__wfSlice = true;
    scene.add(mesh);

    // Line on top
    const linePositions: number[] = [];
    const lineColors: number[] = [];
    for (let i = 0; i <= maxBin; i++) {
      const cpm = i * freqResCpm;
      const x = (cpm / xMaxCpm) * 2 - 1;
      const y = (amps[i] / globalMax) * 1.2;
      linePositions.push(x, y, zPos);
      const [r, g, b] = getColor(amps[i]);
      lineColors.push(r, g, b);
    }
    const lineGeo = new THREE.BufferGeometry();
    lineGeo.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3));
    lineGeo.setAttribute('color', new THREE.Float32BufferAttribute(lineColors, 3));
    const lineMat = new THREE.LineBasicMaterial({ vertexColors: true });
    const line = new THREE.Line(lineGeo, lineMat);
    (line as any).__wfSlice = true;
    scene.add(line);

    // Slice label sprite — show timestamp, or "Current" for si=0
    const sliceDisplayLabel = (() => {
      if (si === 0) return 'Current';
      const ma = slice.measured_at;
      if (!ma) return slice.label;
      const [datePart, timePart] = ma.split(' ');
      const [y, m, d] = datePart.split('/');
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      let h = parseInt(timePart?.split(':')[0] ?? '0');
      const min = timePart?.split(':')[1] ?? '00';
      const ampm = h >= 12 ? 'PM' : 'AM';
      h = h % 12 || 12;
      return `${months[parseInt(m)-1]} ${parseInt(d)}, ${y}  ${h}:${min} ${ampm}`;
    })();
    const labelCanvas = document.createElement('canvas');
    labelCanvas.width = 512; labelCanvas.height = 64;
    const lctx = labelCanvas.getContext('2d')!;
    lctx.font = 'bold 22px Inter,sans-serif';
    lctx.fillStyle = si === 0 ? '#0f766e' : '#374151';
    lctx.fillText(sliceDisplayLabel, 4, 44);
    const texture = new THREE.CanvasTexture(labelCanvas);
    const spriteMat = new THREE.SpriteMaterial({ map: texture, transparent: true });
    const sprite = new THREE.Sprite(spriteMat);
    sprite.scale.set(1.2, 0.15, 1);
    sprite.position.set(-0.85, -0.12, zPos);
    (sprite as any).__wfLabel = true;
    scene.add(sprite);
  }

  // Waterfall markers as vertical planes
  for (const m of waterfallMarkers) {
    const markerCpms: Array<{ cpm: number; label: string; colour: string }> = [];
    if (m.type === 'standalone') {
      markerCpms.push({ cpm: m.cpm, label: m.label, colour: m.colour });
    } else if (m.type === 'harmonic' && m.fundamental && m.count) {
      for (let h = 1; h <= m.count; h++) markerCpms.push({ cpm: m.fundamental * h, label: `h${h}`, colour: m.colour });
    } else if (m.type === 'sideband' && m.centre && m.spacing && m.sbCount) {
      markerCpms.push({ cpm: m.centre, label: m.label || 'SB', colour: m.colour });
      for (let s = 1; s <= m.sbCount; s++) {
        markerCpms.push({ cpm: m.centre + m.spacing * s, label: `+${s}`, colour: m.colour });
        markerCpms.push({ cpm: m.centre - m.spacing * s, label: `-${s}`, colour: m.colour });
      }
    }
    for (const { cpm, label, colour } of markerCpms) {
      if (cpm <= 0 || cpm > xMaxCpm) continue;
      const x = (cpm / xMaxCpm) * 2 - 1;
      const planeGeo = new THREE.PlaneGeometry(0.005, 1.4);
      const planeMat = new THREE.MeshBasicMaterial({
        color: new THREE.Color(colour), transparent: true, opacity: 0.5, side: THREE.DoubleSide,
      });
      const plane = new THREE.Mesh(planeGeo, planeMat);
      plane.position.set(x, 0.7, -1);
      (plane as any).__wfMarker = true;
      scene.add(plane);
    }
  }

  // ── X-axis CPM tick labels (front face, z=0.05) ──────────────────────────
  const xTickCount = 5;
  for (let i = 0; i <= xTickCount; i++) {
    const cpmVal = Math.round(xMaxCpm * i / xTickCount);
    const lbl = cpmVal === 0 ? '0' : `${Math.round(cpmVal / 1000)}k`;
    const sp = makeTextSprite(lbl, '#6b7280', 19, 110, 38);
    sp.scale.set(0.22, 0.065, 1);
    sp.position.set((cpmVal / xMaxCpm) * 2 - 1, -0.12, 0.06);
    (sp as any).__wfGrid = true;
    scene.add(sp);
  }
  // "CPM" axis label
  const xLabel = makeTextSprite('← CPM →', '#374151', 21, 200, 40);
  xLabel.scale.set(0.40, 0.07, 1);
  xLabel.position.set(0, -0.26, 0.06);
  (xLabel as any).__wfGrid = true;
  scene.add(xLabel);

  // ── Y-axis amplitude tick labels (left side, x=-1.15) ────────────────────
  const yTickCount = 4;
  for (let i = 0; i <= yTickCount; i++) {
    const amp = globalMax * i / yTickCount;
    const y   = (amp / globalMax) * 1.2;
    const lbl = amp === 0 ? '0' : amp < 0.001 ? amp.toExponential(2) : amp < 0.01 ? amp.toFixed(4) : amp < 0.1 ? amp.toFixed(3) : amp.toFixed(2);
    const sp  = makeTextSprite(lbl, '#6b7280', 18, 160, 36);
    sp.scale.set(0.30, 0.055, 1);
    sp.position.set(-1.20, y, 0.06);
    (sp as any).__wfGrid = true;
    scene.add(sp);
  }
  // Y-axis unit label
  const yLabel = makeTextSprite(`Amplitude (${unit || 'units'})`, '#374151', 21, 300, 42);
  yLabel.scale.set(0.55, 0.075, 1);
  yLabel.position.set(-1.20, 1.35, 0.06);
  (yLabel as any).__wfGrid = true;
  scene.add(yLabel);
}

// ─── Panel components ─────────────────────────────────────────────────────────

interface PanelProps {
  panelId: string;
  signal: Signal;
  group: TWFGroup | null;
  operatingRpm: number | null;
  isVfd: boolean;
  canRemove: boolean;
  onRemove: () => void;
  onSignalChange: (s: Signal) => void;
  style?: React.CSSProperties;
}

function FFTPanelView({ panelId, signal, group, operatingRpm, canRemove, onRemove, onSignalChange, style }: PanelProps) {
  const [traces, setTraces]       = useState<Record<string, FFTTrace | null>>({});
  const [loading, setLoading]     = useState<Record<string, boolean>>({});
  const [errors, setErrors]       = useState<Record<string, boolean>>({});
  const [xMaxCpm, setXMaxCpm]     = useState(50000);
  const [cursorCpm, setCursorCpm] = useState<number | null>(null);
  const [mousePos, setMousePos]   = useState<{ x: number; y: number } | null>(null);
  const [activeAxes, setActiveAxes] = useState(new Set(['H', 'V', 'A']));
  const [showRev, setShowRev]     = useState(false);
  const [markers, setMarkers]     = useState<AnalysisMarker[]>([]);
  const [ctxMenu, setCtxMenu]     = useState<{ x: number; y: number; cpm: number } | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cacheRef  = useRef<Record<string, FFTTrace>>({});

  useEffect(() => {
    if (!group) return;
    const newLoad: Record<string, boolean> = {};
    const newTraces: Record<string, FFTTrace | null> = {};
    group.axes.forEach(ax => {
      const key = `${ax.file_id}:${ax.axis_id}:${signal}`;
      if (cacheRef.current[key]) newTraces[ax.axis_label] = cacheRef.current[key];
      else newLoad[ax.axis_label] = true;
    });
    setTraces(newTraces); setLoading(newLoad); setErrors({});
    group.axes.forEach(ax => {
      const key = `${ax.file_id}:${ax.axis_id}:${signal}`;
      if (cacheRef.current[key]) return;
      apiFetch<FFTTrace>(`/measurements/fft?file_id=${ax.file_id}&axis_id=${ax.axis_id}&signal=${signal}`)
        .then(d => {
          cacheRef.current[key] = d;
          setTraces(p => ({ ...p, [ax.axis_label]: d }));
          setErrors(p => { const n = { ...p }; delete n[ax.axis_label]; return n; });
        })
        // W4: a fetch failure must be distinguishable from an empty response —
        // flag it so the canvas can say "failed to load" instead of "No FFT data".
        .catch(() => {
          setTraces(p => ({ ...p, [ax.axis_label]: null }));
          setErrors(p => ({ ...p, [ax.axis_label]: true }));
        })
        .finally(() => setLoading(p => { const n = { ...p }; delete n[ax.axis_label]; return n; }));
    });
  }, [group, signal]);

  const visibleTraces = useMemo(() =>
    Object.entries(traces).filter(([l, t]) => t && activeAxes.has(l)).map(([, t]) => t as FFTTrace),
    [traces, activeAxes]);

  const drawFFT = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!visibleTraces.length) {
      const dpr = window.devicePixelRatio || 1;
      const W = canvas.offsetWidth, H = canvas.offsetHeight;
      if (!W || !H) return;
      canvas.width = W * dpr; canvas.height = H * dpr;
      const ctx = canvas.getContext('2d')!; ctx.scale(dpr, dpr);
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, W, H);
      const anyLoading = Object.keys(loading).length > 0;
      const anyError = Object.keys(errors).length > 0;
      ctx.fillStyle = anyError && !anyLoading ? '#dc2626' : '#9ca3af';
      ctx.font = '13px Inter,sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(
        anyLoading ? 'Loading FFT…' : anyError ? 'FFT failed to load' : 'No FFT data',
        W / 2, H / 2,
      );
      return;
    }
    renderFFTCanvas(canvasRef.current!, visibleTraces, xMaxCpm, markers, cursorCpm, operatingRpm ?? null, showRev);
  }, [visibleTraces, xMaxCpm, markers, cursorCpm, operatingRpm, showRev, loading, errors]);

  useEffect(() => { drawFFT(); }, [drawFFT]);

  const onMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - r.left - 58;
    const cW = r.width - 74;
    if (x >= 0 && x <= cW) {
      setCursorCpm((x / cW) * xMaxCpm);
      setMousePos({ x: e.clientX, y: e.clientY });
    } else {
      setCursorCpm(null);
      setMousePos(null);
    }
  };

  const onContextMenu = (e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const r = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - r.left - 58;
    const cW = r.width - 74;
    setCtxMenu({ x: e.clientX, y: e.clientY, cpm: Math.max(0, Math.round((x / cW) * xMaxCpm)) });
  };

  const firstTrace = Object.values(traces).find(t => t != null);

  return (
    <div style={style} className="flex flex-col bg-white border-b border-gray-100 min-h-0 overflow-hidden">
      {/* Title bar */}
      <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border-b border-gray-100 shrink-0">
        <span className="text-xs font-semibold text-gray-700">FFT — {signalLabel(signal)} ({signalUnit(signal)})</span>
        <div className="flex rounded overflow-hidden border border-gray-200 text-xs ml-2">
          {SIGNALS.map((s, i) => (
            <button key={s} onClick={() => onSignalChange(s)}
              className={`px-2 py-0.5 ${i > 0 ? 'border-l border-gray-200' : ''} ${signal === s ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >{signalLabel(s)}</button>
          ))}
        </div>
        <div className="flex gap-1 ml-1">
          {['H','V','A'].map(l => (
            <button key={l} onClick={() => setActiveAxes(p => { const n = new Set(p); if (n.has(l)) { if (n.size > 1) n.delete(l); } else n.add(l); return n; })}
              className={`text-xs px-1.5 py-0.5 rounded border ${activeAxes.has(l) ? 'bg-white border-gray-300 text-gray-700' : 'border-gray-100 text-gray-300 bg-gray-50'}`}
              style={{ color: activeAxes.has(l) ? AXIS_COLORS[l] : undefined }}
            >{l}</button>
          ))}
        </div>
        {operatingRpm && operatingRpm > 0 && (
          <button onClick={() => setShowRev(v => !v)}
            className={`ml-1 text-xs px-2 py-0.5 rounded border ${showRev ? 'bg-slate-700 border-slate-600 text-slate-200' : 'border-gray-200 text-gray-500'}`}
          >1× Harmonics</button>
        )}
        <div className="ml-auto flex items-center gap-2">
          {firstTrace && <span className="text-xs text-gray-400 font-mono">{firstTrace.freq_res_cpm.toFixed(1)} CPM/bin</span>}
          {canRemove && <button onClick={onRemove} className="text-gray-300 hover:text-red-400 transition-colors"><X size={13} /></button>}
        </div>
      </div>
      {/* Canvas area */}
      <div className="flex flex-1 min-h-0 gap-2 p-2">
        <div className="flex-1 min-w-0 flex flex-col">
          {markers.length > 0 && (
            <div className="flex justify-end mb-1 shrink-0">
              <button
                onClick={() => setMarkers(prev => prev.slice(0, -1))}
                className="text-xs px-2.5 py-1 bg-white border border-red-200 text-red-500 rounded hover:bg-red-50 transition-colors flex items-center gap-1"
              >↩ Clear last marker</button>
            </div>
          )}
          <div className="relative rounded border border-gray-100 flex-1 min-h-0">
            <canvas
              ref={canvasRef}
              style={{ width: '100%', height: '100%', display: 'block', cursor: 'crosshair' }}
              onMouseMove={onMouseMove}
              onMouseLeave={() => { setCursorCpm(null); setMousePos(null); }}
              onContextMenu={onContextMenu}
            />
            {mousePos && cursorCpm !== null && (
              <div
                className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs pointer-events-none"
                style={{ left: mousePos.x + 14, top: mousePos.y - 12 }}
              >
                <div className="font-semibold text-gray-700 mb-1">{Math.round(cursorCpm).toLocaleString()} CPM</div>
                {Object.entries(traces).filter(([l, t]) => t && activeAxes.has(l)).map(([l, t]) => {
                  const binIdx = Math.min(Math.round(cursorCpm / t!.freq_res_cpm), t!.amplitudes.length - 1);
                  return (
                    <div key={l} className="flex items-center gap-2 py-0.5">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: AXIS_COLORS[l] }} />
                      <span className="text-gray-500 w-3">{l}</span>
                      <span className="font-mono text-gray-700">{t!.amplitudes[binIdx].toFixed(5)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1 px-1">
            <span className="text-xs text-gray-400 shrink-0">0–{xMaxCpm >= 1000 ? `${xMaxCpm/1000}k` : xMaxCpm} CPM</span>
            <input type="range" min={1000} max={300000} step={1000} value={xMaxCpm}
              onChange={e => setXMaxCpm(Number(e.target.value))}
              className="flex-1 h-1.5 rounded accent-teal-600"
            />
            <div className="flex gap-0.5">
              {[10000,25000,50000,100000,200000,300000].map(v => (
                <button key={v} onClick={() => setXMaxCpm(v)}
                  className={`text-xs px-1.5 py-0.5 rounded border ${xMaxCpm === v ? 'bg-teal-600 text-white border-teal-600' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                >{v/1000}k</button>
              ))}
            </div>
          </div>
        </div>
        {/* Info panel */}
        <div className="w-36 shrink-0 text-xs space-y-2 pt-1">
          {['H','V','A'].map(l => {
            const t = traces[l]; const col = AXIS_COLORS[l]; const act = activeAxes.has(l);
            const rms = t ? (t.amplitudes.reduce((s, v) => s + v * v, 0) / t.amplitudes.length) ** 0.5 : null;
            return (
              <div key={l} className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full border-2 shrink-0" style={{ backgroundColor: act ? col : 'transparent', borderColor: col }} />
                <span className={act ? 'text-gray-700' : 'text-gray-300'}>{l}</span>
                <span className="ml-auto font-mono text-gray-400">{t && rms != null ? rms.toFixed(4) : loading[l] ? '…' : '—'}</span>
              </div>
            );
          })}
          {cursorCpm !== null && (
            <div className="border-t border-gray-100 pt-1 space-y-0.5">
              <div className="text-gray-600 font-medium">{Math.round(cursorCpm).toLocaleString()} CPM</div>
              {Object.entries(traces).filter(([l, t]) => t && activeAxes.has(l)).map(([l, t]) => {
                const binIdx = Math.min(Math.round(cursorCpm / t!.freq_res_cpm), t!.amplitudes.length - 1);
                return <div key={l} className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: AXIS_COLORS[l] }} /><span className="font-mono text-gray-600">{t!.amplitudes[binIdx].toFixed(5)}</span></div>;
              })}
            </div>
          )}
          {markers.length > 0 && (
            <div className="border-t border-gray-100 pt-1 space-y-0.5">
              <div className="text-gray-400">Markers</div>
              {markers.map(m => (
                <div key={m.id} className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-sm" style={{ backgroundColor: m.colour }} />
                  <span className="flex-1 truncate text-gray-500">{m.label}</span>
                  <button onClick={() => setMarkers(p => p.filter(x => x.id !== m.id))} className="text-gray-300 hover:text-red-400 text-xs">×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Context menu */}
      {ctxMenu && (
        <div className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-xl text-sm min-w-[180px]"
          style={{ left: ctxMenu.x, top: ctxMenu.y }}
          onMouseLeave={() => setCtxMenu(null)}
        >
          <div className="px-3 py-2 text-xs text-gray-400 border-b border-gray-100 font-mono">{Math.round(ctxMenu.cpm).toLocaleString()} CPM</div>
          {(['standalone','harmonic','sideband'] as MarkerType[]).map(t => (
            <button key={t} className="w-full text-left px-3 py-2 hover:bg-gray-50 text-gray-700 text-xs"
              onClick={() => {
                const cpm = ctxMenu.cpm;
                if (t === 'standalone') setMarkers(p => [...p, { id: uid(), type: 'standalone', cpm, label: Math.round(cpm).toLocaleString(), colour: '#7c3aed' }]);
                else if (t === 'harmonic') setMarkers(p => [...p, { id: uid(), type: 'harmonic', cpm, fundamental: cpm, count: 10, label: `${Math.round(cpm/1000)}k`, colour: '#dc2626' }]);
                else setMarkers(p => [...p, { id: uid(), type: 'sideband', cpm, centre: cpm, spacing: 1000, sbCount: 3, label: 'SB', colour: '#d97706' }]);
                setCtxMenu(null);
              }}
            >Add {t} marker</button>
          ))}
          <div className="border-t border-gray-100"><button className="w-full text-left px-3 py-2 hover:bg-gray-50 text-red-500 text-xs" onClick={() => { setMarkers([]); setCtxMenu(null); }}>Clear all markers</button></div>
        </div>
      )}
    </div>
  );
}

// ─── TWF panel ────────────────────────────────────────────────────────────────

function TWFPanelView({ signal, group, operatingRpm, isVfd, canRemove, onRemove, onSignalChange, style }: PanelProps) {
  const [traces, setTraces]     = useState<Record<string, TWFTrace | null>>({});
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(false);
  const [zoomRange, setZoomRange] = useState<[number, number]>([0, 1]);
  const [cursorPct, setCursorPct] = useState<number | null>(null);
  const [activeAxes, setActiveAxes] = useState(new Set(['H', 'V', 'A']));
  const [showRev, setShowRev]   = useState(false);
  const mainRef = useRef<HTMLCanvasElement>(null);
  const navRef  = useRef<HTMLCanvasElement>(null);
  const dragRef = useRef<{ startX: number; startZoom: [number, number]; handle: 'left' | 'right' | 'mid' } | null>(null);
  const cacheRef = useRef<Record<string, TWFTrace>>({});

  const signalType = signal === 'accel' ? 0 : 6;

  useEffect(() => {
    if (!group) return;
    setLoading(true); setZoomRange([0, 1]); setError(false);
    const errored: string[] = [];
    Promise.all(group.axes.map(ax => {
      const key = `${ax.file_id}:${ax.axis_id}:${signal}`;
      if (cacheRef.current[key]) return Promise.resolve([ax.axis_label, cacheRef.current[key]] as [string, TWFTrace]);
      const url = signal === 'velocity'
        ? `/measurements/twf?file_id=${ax.file_id}&axis_id=${ax.axis_id}&signal=velocity`
        : `/measurements/waveform?file_id=${ax.file_id}&axis_id=${ax.axis_id}&wf_type=twf&signal_type=${signalType}`;
      return apiFetch<TWFTrace>(url)
        .then(d => { cacheRef.current[key] = d; return [ax.axis_label, d] as [string, TWFTrace]; })
        // W4: track fetch failures so an empty canvas can say "failed to load"
        // rather than the ambiguous "No data".
        .catch(() => { errored.push(ax.axis_label); return [ax.axis_label, null] as [string, null]; });
    })).then(results => {
      const t: Record<string, TWFTrace | null> = {};
      for (const [l, d] of results) t[l] = d;
      setTraces(t);
      setError(errored.length > 0);
    }).finally(() => setLoading(false));
  }, [group, signal]);

  const activeTraces = useMemo(() =>
    Object.entries(traces).filter(([l, t]) => t && activeAxes.has(l)) as [string, TWFTrace][],
    [traces, activeAxes]);

  const draw = useCallback(() => {
    const canvas = mainRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const W = canvas.offsetWidth, H = canvas.offsetHeight;
    if (!W || !H) return;
    canvas.width = W * dpr; canvas.height = H * dpr;
    const ctx = canvas.getContext('2d')!; ctx.scale(dpr, dpr);
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, W, H);
    if (!activeTraces.length) {
      const failed = error && !loading;
      ctx.fillStyle = failed ? '#dc2626' : '#9ca3af'; ctx.font = '13px Inter,sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(loading ? 'Loading…' : failed ? 'TWF failed to load' : 'No data', W / 2, H / 2); return;
    }
    const PAD = { top: 8, bottom: 20, left: 48, right: 8 };
    const cW = W - PAD.left - PAD.right, cH = H - PAD.top - PAD.bottom;
    let yMin = Infinity, yMax = -Infinity;
    for (const [, t] of activeTraces) for (const v of t.y) { if (v < yMin) yMin = v; if (v > yMax) yMax = v; }
    const yPad = (yMax - yMin || 1) * 0.08; yMin -= yPad; yMax += yPad;
    const n = activeTraces[0][1].y.length, sr = activeTraces[0][1].sample_rate;
    const iStart = Math.floor(zoomRange[0] * n), iEnd = Math.ceil(zoomRange[1] * n);
    ctx.strokeStyle = '#e5e7eb'; ctx.lineWidth = 0.5;
    for (let i = 0; i <= 5; i++) {
      const y = PAD.top + (i / 5) * cH;
      ctx.beginPath(); ctx.moveTo(PAD.left, y); ctx.lineTo(PAD.left + cW, y); ctx.stroke();
      ctx.fillStyle = '#6b7280'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'right';
      ctx.fillText((yMax - (i / 5) * (yMax - yMin)).toFixed(3), PAD.left - 3, y + 3);
    }
    ctx.fillStyle = '#6b7280'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'center';
    for (let i = 0; i <= 4; i++) {
      const t0 = iStart / sr, t1 = iEnd / sr;
      ctx.fillText((t0 + (i / 4) * (t1 - t0)).toFixed(3) + 's', PAD.left + (i / 4) * cW, H - 4);
    }
    if (yMin < 0 && yMax > 0) {
      const zeroY = PAD.top + (yMax / (yMax - yMin)) * cH;
      ctx.strokeStyle = '#d1d5db'; ctx.lineWidth = 1; ctx.setLineDash([4, 4]);
      ctx.beginPath(); ctx.moveTo(PAD.left, zeroY); ctx.lineTo(PAD.left + cW, zeroY); ctx.stroke();
      ctx.setLineDash([]);
    }
    if (showRev && operatingRpm && operatingRpm > 0) {
      const revSec = 60 / operatingRpm, t0 = iStart / sr, t1 = iEnd / sr;
      const firstRev = Math.ceil(t0 / revSec) * revSec;
      ctx.strokeStyle = '#9ca3af'; ctx.lineWidth = 1; ctx.globalAlpha = 0.6; ctx.setLineDash([3, 4]);
      for (let t = firstRev; t <= t1; t += revSec) {
        const xf = PAD.left + ((t - t0) / (t1 - t0)) * cW;
        ctx.beginPath(); ctx.moveTo(xf, PAD.top); ctx.lineTo(xf, PAD.top + cH); ctx.stroke();
      }
      ctx.setLineDash([]); ctx.globalAlpha = 1;
    }
    for (const [label, trace] of activeTraces) {
      const color = AXIS_COLORS[label] || '#94a3b8';
      const dec: Array<[number, number]> = new Array(cW);
      const count = iEnd - iStart;
      for (let px = 0; px < cW; px++) {
        const lo = iStart + Math.floor((px / cW) * count);
        const hi = iStart + Math.floor(((px + 1) / cW) * count);
        let mn = Infinity, mx = -Infinity;
        for (let i = lo; i < hi && i < trace.y.length; i++) { if (trace.y[i] < mn) mn = trace.y[i]; if (trace.y[i] > mx) mx = trace.y[i]; }
        dec[px] = [mn === Infinity ? 0 : mn, mx === -Infinity ? 0 : mx];
      }
      ctx.strokeStyle = color; ctx.lineWidth = 1; ctx.beginPath();
      for (let px = 0; px < dec.length; px++) {
        const [mn, mx] = dec[px], x = PAD.left + px;
        const y1 = PAD.top + ((yMax - mx) / (yMax - yMin)) * cH;
        const y2 = PAD.top + ((yMax - mn) / (yMax - yMin)) * cH;
        if (px === 0) ctx.moveTo(x, (y1 + y2) / 2);
        ctx.lineTo(x, y1); ctx.lineTo(x, y2);
      }
      ctx.stroke();
    }
    if (cursorPct !== null) {
      const cx = PAD.left + cursorPct * cW;
      ctx.strokeStyle = 'rgba(55,65,81,0.4)'; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(cx, PAD.top); ctx.lineTo(cx, PAD.top + cH); ctx.stroke();
    }
  }, [activeTraces, zoomRange, cursorPct, loading, showRev, operatingRpm, error]);

  const drawNav = useCallback(() => {
    const canvas = navRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const W = canvas.offsetWidth, H = canvas.offsetHeight;
    if (!W || !H) return;
    canvas.width = W * dpr; canvas.height = H * dpr;
    const ctx = canvas.getContext('2d')!; ctx.scale(dpr, dpr);
    ctx.fillStyle = '#f9fafb'; ctx.fillRect(0, 0, W, H);
    if (!activeTraces.length) return;
    let yMin = Infinity, yMax = -Infinity;
    for (const [, t] of activeTraces) for (const v of t.y) { if (v < yMin) yMin = v; if (v > yMax) yMax = v; }
    const yr = yMax - yMin || 1; yMin -= yr * 0.05; yMax += yr * 0.05;
    const n = activeTraces[0][1].y.length;
    for (const [label, trace] of activeTraces) {
      const color = AXIS_COLORS[label] || '#94a3b8';
      ctx.strokeStyle = color; ctx.globalAlpha = 0.7; ctx.lineWidth = 1; ctx.beginPath();
      for (let px = 0; px < W; px++) {
        const lo = Math.floor((px / W) * n), hi = Math.floor(((px + 1) / W) * n);
        let mn = Infinity, mx = -Infinity;
        for (let i = lo; i < hi && i < trace.y.length; i++) { if (trace.y[i] < mn) mn = trace.y[i]; if (trace.y[i] > mx) mx = trace.y[i]; }
        const y1 = H - ((Math.max(mx, mn) - yMin) / (yMax - yMin)) * H;
        const y2 = H - ((Math.min(mx, mn) - yMin) / (yMax - yMin)) * H;
        if (px === 0) ctx.moveTo(px, (y1 + y2) / 2); ctx.lineTo(px, y1); ctx.lineTo(px, y2);
      }
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    const [z0, z1] = zoomRange;
    ctx.fillStyle = 'rgba(209,213,219,0.55)'; ctx.fillRect(0, 0, z0 * W, H); ctx.fillRect(z1 * W, 0, W - z1 * W, H);
    ctx.strokeStyle = 'rgba(107,114,128,0.6)'; ctx.lineWidth = 1; ctx.strokeRect(z0 * W, 0, (z1 - z0) * W, H);
    ctx.fillStyle = 'rgba(107,114,128,0.8)'; ctx.fillRect(z0 * W - 2, 0, 4, H); ctx.fillRect(z1 * W - 2, 0, 4, H);
  }, [activeTraces, zoomRange]);

  useEffect(() => { draw(); drawNav(); }, [draw, drawNav]);

  const onNavDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    const [z0, z1] = zoomRange;
    let handle: 'left' | 'right' | 'mid' | null = null;
    if (Math.abs(pct - z0) < 0.02) handle = 'left';
    else if (Math.abs(pct - z1) < 0.02) handle = 'right';
    else if (pct > z0 && pct < z1) handle = 'mid';
    if (handle) dragRef.current = { startX: pct, startZoom: [z0, z1], handle };
    else {
      const span = z1 - z0, half = span / 2;
      const nz0 = Math.max(0, Math.min(1 - span, pct - half));
      setZoomRange([nz0, nz0 + span]);
    }
  };
  const onNavMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!dragRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    const delta = pct - dragRef.current.startX;
    const [z0, z1] = dragRef.current.startZoom;
    const MIN = 0.02;
    if (dragRef.current.handle === 'left') setZoomRange([Math.max(0, Math.min(z1 - MIN, z0 + delta)), z1]);
    else if (dragRef.current.handle === 'right') setZoomRange([z0, Math.min(1, Math.max(z0 + MIN, z1 + delta))]);
    else { const span = z1 - z0; const nz0 = Math.max(0, Math.min(1 - span, z0 + delta)); setZoomRange([nz0, nz0 + span]); }
  };

  const firstTrace = activeTraces[0]?.[1];
  const revPeriodSec = (operatingRpm && operatingRpm > 0) ? 60 / operatingRpm : null;
  const revsInRec = (revPeriodSec && firstTrace) ? Math.floor(firstTrace.duration_sec / revPeriodSec) : 0;

  return (
    <div style={style} className="flex flex-col bg-white border-b border-gray-100 min-h-0 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border-b border-gray-100 shrink-0">
        <span className="text-xs font-semibold text-gray-700">TWF — {signalLabel(signal)} ({signalUnit(signal)})</span>
        <div className="flex rounded overflow-hidden border border-gray-200 text-xs ml-2">
          {(['accel','envelope'] as Signal[]).map((s, i) => (
            <button key={s} onClick={() => onSignalChange(s)}
              className={`px-2 py-0.5 ${i > 0 ? 'border-l border-gray-200' : ''} ${signal === s ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >{signalLabel(s)}</button>
          ))}
        </div>
        <div className="flex gap-1 ml-1">
          {['H','V','A'].map(l => (
            <button key={l} onClick={() => setActiveAxes(p => { const n = new Set(p); if (n.has(l)) { if (n.size > 1) n.delete(l); } else n.add(l); return n; })}
              className={`text-xs px-1.5 py-0.5 rounded border ${activeAxes.has(l) ? 'bg-white border-gray-300' : 'border-gray-100 text-gray-300 bg-gray-50'}`}
              style={{ color: activeAxes.has(l) ? AXIS_COLORS[l] : undefined }}
            >{l}</button>
          ))}
        </div>
        {operatingRpm && operatingRpm > 0 && (
          <button onClick={() => setShowRev(v => !v)}
            className={`ml-1 text-xs px-2 py-0.5 rounded border ${showRev ? 'bg-slate-700 border-slate-600 text-slate-200' : 'border-gray-200 text-gray-500'}`}
          >Rev markers</button>
        )}
        <div className="ml-auto flex items-center gap-2">
          {revPeriodSec && <span className="text-xs text-gray-400 font-mono">1rev={revPeriodSec.toFixed(4)}s{revsInRec > 0 ? ` · ${revsInRec} revs` : ''}</span>}
          {canRemove && <button onClick={onRemove} className="text-gray-300 hover:text-red-400 transition-colors"><X size={13} /></button>}
        </div>
      </div>
      <div className="flex-1 min-h-0 flex flex-col p-2 gap-1">
        <div className="relative rounded border border-gray-100 flex-1 min-h-0">
          <canvas ref={mainRef} style={{ width: '100%', height: '100%', display: 'block', cursor: 'crosshair' }}
            onMouseMove={e => { const r = e.currentTarget.getBoundingClientRect(); const x = e.clientX - r.left - 48; const cW = r.width - 56; setCursorPct(x >= 0 && x <= cW ? x / cW : null); }}
            onMouseLeave={() => setCursorPct(null)}
            onWheel={e => { e.preventDefault(); const rect = e.currentTarget.getBoundingClientRect(); const xPct = (e.clientX - rect.left - 48) / (rect.width - 56); const focus = zoomRange[0] + xPct * (zoomRange[1] - zoomRange[0]); const factor = e.deltaY > 0 ? 1.2 : 0.833; const span = Math.max(0.01, Math.min(1, (zoomRange[1] - zoomRange[0]) * factor)); const z0 = Math.max(0, Math.min(1 - span, focus - xPct * span)); setZoomRange([z0, z0 + span]); }}
          />
        </div>
        <div className="rounded overflow-hidden border border-gray-100 h-10 shrink-0">
          <canvas ref={navRef} style={{ width: '100%', height: '100%', display: 'block', cursor: 'ew-resize' }}
            onMouseDown={onNavDown} onMouseMove={onNavMove} onMouseUp={() => dragRef.current = null} onMouseLeave={() => dragRef.current = null}
          />
        </div>
        {zoomRange[0] > 0.001 || zoomRange[1] < 0.999 ? (
          <button onClick={() => setZoomRange([0, 1])} className="self-end text-xs px-2 py-0.5 border border-gray-200 rounded text-gray-500 hover:bg-gray-50">Reset Zoom</button>
        ) : null}
      </div>
    </div>
  );
}

// ─── Waterfall view ───────────────────────────────────────────────────────────

interface WaterfallViewProps {
  machineId: string;
  currentGroup: TWFGroup | null;
  operatingRpm: number | null;
}

function WaterfallView({ machineId, currentGroup, operatingRpm }: WaterfallViewProps) {
  const [configured, setConfigured]   = useState(false);
  const [wfConfig, setWfConfig]       = useState({ pointName: '', axisLabel: 'H', daysBack: 60, nComparisons: 3, signal: 'accel' as Signal });
  const [wfData, setWfData]           = useState<WaterfallData | null>(null);
  const [wfLoading, setWfLoading]     = useState(false);
  const [wfError, setWfError]         = useState('');
  const [xMaxCpm, setXMaxCpm]         = useState(50000);
  const [toast, setToast]             = useState('');
  const [sliceCtxMenu, setSliceCtxMenu] = useState<{ x: number; y: number; sliceIdx: number } | null>(null);
  const [swapPicker, setSwapPicker]   = useState<{ sliceIdx: number; candIdx: number } | null>(null);
  const [wfMarkers, setWfMarkers]     = useState<AnalysisMarker[]>([]);
  const [wfCtxMenu, setWfCtxMenu]     = useState<{ x: number; y: number; cpm: number } | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const threeRef  = useRef<{ renderer: THREE.WebGLRenderer; scene: THREE.Scene; camera: THREE.PerspectiveCamera; animId: number } | null>(null);
  const orbitRef  = useRef({ theta: -0.5, phi: 0.35, radius: 4 });
  const dragRef   = useRef<{ x: number; y: number; button: number } | null>(null);

  // Collect points/axes from current group
  const availablePoints = useMemo(() => {
    if (!currentGroup) return [];
    return [{ name: currentGroup.point_name, axes: currentGroup.axes.map(ax => ax.axis_label) }];
  }, [currentGroup]);

  const currentAxisId = useMemo(() => {
    if (!currentGroup || !wfConfig.axisLabel) return null;
    return currentGroup.axes.find(ax => ax.axis_label === wfConfig.axisLabel)?.axis_id ?? null;
  }, [currentGroup, wfConfig.axisLabel]);

  const currentFileId = useMemo(() => {
    if (!currentGroup || !wfConfig.axisLabel) return null;
    return currentGroup.axes.find(ax => ax.axis_label === wfConfig.axisLabel)?.file_id ?? null;
  }, [currentGroup, wfConfig.axisLabel]);

  // Init config from current group
  useEffect(() => {
    if (currentGroup && !wfConfig.pointName) {
      setWfConfig(p => ({ ...p, pointName: currentGroup.point_name }));
    }
  }, [currentGroup]);

  const generateWaterfall = async () => {
    if (!currentAxisId || !currentFileId) { setWfError('No axis selected'); return; }
    setWfLoading(true); setWfError('');
    try {
      const data = await apiFetch<WaterfallData>(
        `/measurements/waterfall?axis_id=${currentAxisId}&days_back=${wfConfig.daysBack}&n_comparisons=${wfConfig.nComparisons}&current_file_id=${currentFileId}&signal=${wfConfig.signal}`
      );
      setWfData(data);
      setConfigured(true);
    } catch (e: any) {
      setWfError(e?.message || 'Failed to load waterfall data');
    } finally {
      setWfLoading(false);
    }
  };

  function updateCamera(camera: THREE.PerspectiveCamera) {
    const { theta, phi, radius } = orbitRef.current;
    const x = radius * Math.sin(theta) * Math.cos(phi);
    const y = radius * Math.sin(phi);
    const z = radius * Math.cos(theta) * Math.cos(phi);
    camera.position.set(x, y, z);
    camera.lookAt(0, 0.3, -1);
  }

  // Three.js setup + rebuild on data/xMaxCpm/markers change
  useEffect(() => {
    if (!configured || !wfData || !canvasRef.current) return;
    const canvas = canvasRef.current;
    let renderer = threeRef.current?.renderer;
    let scene    = threeRef.current?.scene;
    let camera   = threeRef.current?.camera;

    if (!renderer) {
      scene    = new THREE.Scene();
      scene.background = new THREE.Color(0xf8fafc);
      camera   = new THREE.PerspectiveCamera(45, canvas.clientWidth / Math.max(canvas.clientHeight, 1), 0.1, 1000);
      renderer = new THREE.WebGLRenderer({ canvas, antialias: true, preserveDrawingBuffer: true });
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.setSize(canvas.clientWidth, canvas.clientHeight);
      threeRef.current = { renderer, scene, camera, animId: 0 };
    }

    buildWaterfallScene(scene!, wfData.slices, xMaxCpm, wfData.freq_res_cpm, wfMarkers, wfData.unit);
    updateCamera(camera!);
    renderer.render(scene!, camera!);

    return () => {
      if (threeRef.current) {
        cancelAnimationFrame(threeRef.current.animId);
      }
    };
  }, [configured, wfData, xMaxCpm, wfMarkers]);

  // Canvas mouse handlers (manual orbit)
  const onCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    dragRef.current = { x: e.clientX, y: e.clientY, button: e.button };
    if (e.button === 2) {
      const rect = e.currentTarget.getBoundingClientRect();
      const px = (e.clientX - rect.left - 58) / (rect.width - 74);
      if (px >= 0 && px <= 1) {
        setWfCtxMenu({ x: e.clientX, y: e.clientY, cpm: Math.round(px * xMaxCpm) });
      }
    }
  };
  const onCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!dragRef.current || dragRef.current.button !== 0) return;
    const dx = e.clientX - dragRef.current.x;
    const dy = e.clientY - dragRef.current.y;
    orbitRef.current.theta -= dx * 0.008;
    orbitRef.current.phi    = Math.max(-0.05, Math.min(1.0, orbitRef.current.phi + dy * 0.008));
    dragRef.current = { x: e.clientX, y: e.clientY, button: 0 };
    if (threeRef.current) {
      const { renderer, scene, camera } = threeRef.current;
      updateCamera(camera);
      renderer.render(scene, camera);
    }
  };
  const onCanvasMouseUp = () => { dragRef.current = null; };
  const onCanvasWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    orbitRef.current.radius = Math.max(1.5, Math.min(10, orbitRef.current.radius + e.deltaY * 0.005));
    if (threeRef.current) {
      const { renderer, scene, camera } = threeRef.current;
      updateCamera(camera);
      renderer.render(scene, camera);
    }
  };

  const copyPNG = () => {
    if (!canvasRef.current) return;
    canvasRef.current.toBlob(blob => {
      if (!blob) return;
      navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]).then(() => {
        setToast('Waterfall copied to clipboard (PNG)');
        setTimeout(() => setToast(''), 3000);
      }).catch(() => setToast('Copy failed — try right-clicking the chart'));
    }, 'image/png');
  };

  const swapSlice = async (sliceIdx: number, fileId: number, measuredAt: string, twfRms: number) => {
    if (!wfData) return;
    try {
      const result = await apiFetch<{ file_id: number; measured_at: string; amplitudes: number[]; twf_rms: number }>(
        '/measurements/waterfall/swap-slice',
        { method: 'POST', body: JSON.stringify({ file_id: fileId, axis_id: wfData.axis_id, signal: wfData.signal }) }
      );
      const newSlices = wfData.slices.map((s, i) => i === sliceIdx ? { ...s, file_id: result.file_id, measured_at: result.measured_at, amplitudes: result.amplitudes, twf_rms: result.twf_rms } : s);
      setWfData({ ...wfData, slices: newSlices });
      setWfError('');
    } catch {
      // W4: a swap failure must not be silent — the slice stayed as-is, so say so.
      setWfError('Slice swap failed — the chart was not changed.');
    }
    setSwapPicker(null); setSliceCtxMenu(null);
  };

  // ── Setup form
  if (!configured) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 max-w-lg w-full">
          <h3 className="text-gray-900 font-semibold text-base mb-6">Configure Waterfall</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <label className="text-sm text-gray-600 w-28 shrink-0">Axis</label>
              <div className="flex gap-2">
                {(currentGroup?.axes ?? []).map(ax => (
                  <button key={ax.axis_label} onClick={() => setWfConfig(p => ({ ...p, axisLabel: ax.axis_label }))}
                    className={`px-3 py-1.5 rounded-lg border text-sm ${wfConfig.axisLabel === ax.axis_label ? 'bg-teal-600 text-white border-teal-600' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                  >{ax.axis_label}</button>
                ))}
              </div>
            </div>
            <div className="flex items-start gap-4">
              <label className="text-sm text-gray-600 w-28 shrink-0 pt-0.5">Time window</label>
              <div className="flex flex-wrap gap-2">
                {[30,60,90,180,365].map(d => (
                  <button key={d} onClick={() => setWfConfig(p => ({ ...p, daysBack: d }))}
                    className={`px-3 py-1.5 rounded-lg border text-sm ${wfConfig.daysBack === d ? 'bg-teal-600 text-white border-teal-600' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                  >{d} days</button>
                ))}
              </div>
            </div>
            <div className="flex items-start gap-4">
              <label className="text-sm text-gray-600 w-28 shrink-0 pt-0.5">Comparisons</label>
              <div className="flex gap-2">
                {[2,3,4].map(n => (
                  <button key={n} onClick={() => setWfConfig(p => ({ ...p, nComparisons: n }))}
                    className={`px-3 py-1.5 rounded-lg border text-sm ${wfConfig.nComparisons === n ? 'bg-teal-600 text-white border-teal-600' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                  >{n} slices</button>
                ))}
              </div>
            </div>
            <div className="flex items-start gap-4">
              <label className="text-sm text-gray-600 w-28 shrink-0 pt-0.5">Signal</label>
              <div className="flex gap-2">
                {SIGNALS.map(s => (
                  <button key={s} onClick={() => setWfConfig(p => ({ ...p, signal: s }))}
                    className={`px-3 py-1.5 rounded-lg border text-sm ${wfConfig.signal === s ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                  >{signalLabel(s)} ({signalUnit(s)})</button>
                ))}
              </div>
            </div>
            {currentGroup && <p className="text-xs text-gray-400 pl-32">Current: {fmtEastern(currentGroup.measured_at)}</p>}
            {wfError && <p className="text-sm text-red-500 pl-32">{wfError}</p>}
            <div className="pt-2 pl-28">
              <button onClick={generateWaterfall} disabled={wfLoading}
                className="px-6 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 disabled:opacity-50 transition-colors"
              >{wfLoading ? 'Loading…' : 'Generate Waterfall'}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Waterfall view
  const swapPickerSlice = swapPicker !== null && wfData ? wfData.slices[swapPicker.sliceIdx] : null;
  const swapCandidate   = swapPickerSlice ? swapPickerSlice.candidates[swapPicker!.candIdx] : null;

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      <div className="flex flex-1 min-h-0 gap-0">
        {/* Controls column */}
        <div className="w-44 shrink-0 bg-gray-50 border-r border-gray-100 p-3 flex flex-col gap-3 text-xs overflow-y-auto">
          <div>
            <div className="text-gray-500 font-medium mb-1">Signal</div>
            <div className="text-gray-700">{signalLabel(wfConfig.signal as Signal)} ({signalUnit(wfConfig.signal as Signal)})</div>
          </div>
          <div>
            <div className="text-gray-500 font-medium mb-1">Colour scale</div>
            <div className="h-3 rounded" style={{ background: 'linear-gradient(to right, #fbbf24, #dc2626)' }} />
            <div className="flex justify-between mt-0.5 text-gray-400">
              <span>Low</span><span>High</span>
            </div>
          </div>
          <div className="mt-auto flex flex-col gap-1">
            <button onClick={() => { setConfigured(false); setWfData(null); setWfMarkers([]); if (threeRef.current) { threeRef.current.renderer.dispose(); threeRef.current = null; } }}
              className="w-full px-2 py-1.5 border border-gray-200 rounded text-gray-600 hover:bg-gray-100">Reconfigure</button>
            <button onClick={copyPNG} className="w-full px-2 py-1.5 border border-gray-200 rounded text-gray-600 hover:bg-gray-100">Copy PNG</button>
          </div>
        </div>
        {/* Canvas */}
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 min-h-0 relative">
            <canvas
              ref={canvasRef}
              style={{ width: '100%', height: '100%', display: 'block', cursor: dragRef.current ? 'grabbing' : 'grab' }}
              onMouseDown={onCanvasMouseDown}
              onMouseMove={onCanvasMouseMove}
              onMouseUp={onCanvasMouseUp}
              onMouseLeave={onCanvasMouseUp}
              onWheel={onCanvasWheel}
              onContextMenu={e => { e.preventDefault(); }}
            />
          </div>
          {/* X range */}
          <div className="p-2 border-t border-gray-100 shrink-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-gray-500 shrink-0">0–{xMaxCpm >= 1000 ? `${xMaxCpm/1000}k` : xMaxCpm} CPM</span>
              <input type="range" min={1000} max={300000} step={1000} value={xMaxCpm}
                onChange={e => setXMaxCpm(Number(e.target.value))}
                className="flex-1 h-1.5 rounded accent-teal-600"
              />
            </div>
            <div className="flex gap-1">
              {[10000,25000,50000,100000,200000,300000].map(v => (
                <button key={v} onClick={() => setXMaxCpm(v)}
                  className={`text-xs px-1.5 py-0.5 rounded border ${xMaxCpm === v ? 'bg-teal-600 text-white border-teal-600' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                >{v/1000}k</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Slice table */}
      {wfData && (
        <div className="shrink-0 border-t border-gray-100 bg-white">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 text-gray-500 uppercase tracking-wide border-b border-gray-100">
                <th className="px-3 py-2 text-left w-12">Slice</th>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-right">RMS ({wfData.unit})</th>
                <th className="px-3 py-2 text-right">Days ago</th>
                <th className="px-3 py-2 w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {wfData.slices.map((s, i) => (
                <tr key={i} className="hover:bg-gray-50 transition-colors"
                  onContextMenu={e => { e.preventDefault(); setSliceCtxMenu({ x: e.clientX, y: e.clientY, sliceIdx: i }); }}
                >
                  <td className="px-3 py-1.5 font-mono text-gray-400">
                    {s.amplitudes ? <span className="text-teal-600">▶</span> : '○'} {i}
                  </td>
                  <td className="px-3 py-1.5 text-gray-700">{fmtEastern(s.measured_at)}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-gray-600">{s.twf_rms != null ? s.twf_rms.toFixed(4) : '—'}</td>
                  <td className="px-3 py-1.5 text-right text-gray-500">{s.days_ago === 0 ? 'Current' : `${s.days_ago}`}</td>
                  <td className="px-3 py-1.5">
                    {s.candidates.length > 0 && (
                      <button onClick={() => setSwapPicker({ sliceIdx: i, candIdx: 0 })} className="text-gray-300 hover:text-blue-500" title="Choose different FFT">⇄</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Slice context menu */}
      {sliceCtxMenu && (
        <div className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-xl text-sm min-w-[200px]"
          style={{ left: sliceCtxMenu.x, top: sliceCtxMenu.y }}
          onMouseLeave={() => setSliceCtxMenu(null)}
        >
          <div className="px-3 py-2 text-xs text-gray-400 border-b border-gray-100">Slice {sliceCtxMenu.sliceIdx}</div>
          {wfData?.slices[sliceCtxMenu.sliceIdx]?.candidates.length ? (
            <button className="w-full text-left px-3 py-2 hover:bg-gray-50 text-gray-700 text-xs"
              onClick={() => { setSwapPicker({ sliceIdx: sliceCtxMenu.sliceIdx, candIdx: 0 }); setSliceCtxMenu(null); }}
            >▷ Choose a different FFT</button>
          ) : <div className="px-3 py-2 text-xs text-gray-400">No alternatives available</div>}
        </div>
      )}

      {/* Swap picker modal */}
      {swapPicker !== null && swapPickerSlice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20" onClick={() => setSwapPicker(null)}>
          <div className="bg-white rounded-xl border border-gray-200 shadow-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <h4 className="font-semibold text-gray-900 mb-1 text-sm">Choose FFT — Slot: {swapPickerSlice.label}</h4>
            <p className="text-xs text-gray-400 mb-4">{swapPickerSlice.candidates.length} alternatives available</p>
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => setSwapPicker(p => p ? { ...p, candIdx: Math.max(0, p.candIdx - 1) } : p)} disabled={swapPicker.candIdx === 0} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><ChevronLeft size={16} /></button>
              {swapCandidate && (
                <div className="flex-1 text-center text-sm">
                  <div className="font-medium text-gray-900">{fmtEastern(swapCandidate.measured_at)}</div>
                  <div className="text-gray-500 mt-0.5">RMS: {swapCandidate.twf_rms.toFixed(4)} {wfData?.unit} · {swapCandidate.days_ago} days ago</div>
                </div>
              )}
              <button onClick={() => setSwapPicker(p => p ? { ...p, candIdx: Math.min(swapPickerSlice.candidates.length - 1, p.candIdx + 1) } : p)} disabled={swapPicker.candIdx >= swapPickerSlice.candidates.length - 1} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><ChevronRight size={16} /></button>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setSwapPicker(null)} className="flex-1 px-3 py-2 border border-gray-200 rounded text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
              <button
                onClick={() => swapCandidate && swapSlice(swapPicker.sliceIdx, swapCandidate.file_id, swapCandidate.measured_at, swapCandidate.twf_rms)}
                className="flex-1 px-3 py-2 bg-teal-600 text-white rounded text-sm hover:bg-teal-700"
              >Select</button>
            </div>
          </div>
        </div>
      )}

      {/* Waterfall context menu (for adding markers) */}
      {wfCtxMenu && (
        <div className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-xl text-sm min-w-[180px]"
          style={{ left: wfCtxMenu.x, top: wfCtxMenu.y }}
          onMouseLeave={() => setWfCtxMenu(null)}
        >
          <div className="px-3 py-2 text-xs text-gray-400 border-b border-gray-100 font-mono">{Math.round(wfCtxMenu.cpm).toLocaleString()} CPM</div>
          {(['standalone','harmonic','sideband'] as MarkerType[]).map(t => (
            <button key={t} className="w-full text-left px-3 py-2 hover:bg-gray-50 text-gray-700 text-xs"
              onClick={() => {
                const cpm = wfCtxMenu.cpm;
                if (t === 'standalone') setWfMarkers(p => [...p, { id: uid(), type: 'standalone', cpm, label: Math.round(cpm).toLocaleString(), colour: '#7c3aed' }]);
                else if (t === 'harmonic') setWfMarkers(p => [...p, { id: uid(), type: 'harmonic', cpm, fundamental: cpm, count: 5, label: `${Math.round(cpm/1000)}k`, colour: '#dc2626' }]);
                else setWfMarkers(p => [...p, { id: uid(), type: 'sideband', cpm, centre: cpm, spacing: 1000, sbCount: 3, label: 'SB', colour: '#d97706' }]);
                setWfCtxMenu(null);
              }}
            >Add {t} marker</button>
          ))}
          <div className="border-t border-gray-100"><button className="w-full text-left px-3 py-2 hover:bg-gray-50 text-red-500 text-xs" onClick={() => { setWfMarkers([]); setWfCtxMenu(null); }}>Clear all markers</button></div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-sm px-4 py-2 rounded-lg shadow-lg z-50">{toast}</div>
      )}
    </div>
  );
}

// ─── Main AnalysisView ────────────────────────────────────────────────────────

export function AnalysisView() {
  const [activeTab, setActiveTab]   = useState<TabType>('fft');
  const [panels, setPanels]         = useState<Panel[]>([
    { id: uid(), tabType: 'fft', signal: 'velocity', heightPct: 100 },
  ]);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const addMenuRef = useRef<HTMLDivElement>(null);

  // State from sessionStorage
  const [state] = useState<any>(() => {
    try { return JSON.parse(sessionStorage.getItem('pc_analysis_state') || 'null'); } catch { return null; }
  });
  const machineId   = state?.machineId ?? '';
  const machineName = state?.machineName ?? 'Unknown Machine';
  const operatingRpm = state?.operatingRpm ?? null;
  const isVfd        = state?.isVfd ?? false;

  // Groups (all available measurements)
  const [groups, setGroups]     = useState<TWFGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [selectedDate, setSelectedDate]   = useState(state?.selectedDate ?? '');
  const [selectedPoint, setSelectedPoint] = useState(state?.selectedPoint ?? '');

  useEffect(() => {
    if (!machineId) return;
    apiFetch<TWFGroup[]>(`/measurements/twf-available?machine_id=${machineId}&limit=100`)
      .then(data => {
        setGroups(data || []);
        if (data?.length && !selectedDate) {
          setSelectedDate(data[0].measured_at);
          setSelectedPoint(data[0].point_name);
        }
      })
      .catch(() => {})
      .finally(() => setGroupsLoading(false));
  }, [machineId]);

  const currentGroup = useMemo(() =>
    groups.find(g => g.measured_at === selectedDate && g.point_name === selectedPoint) ||
    groups.find(g => g.measured_at === selectedDate) ||
    groups[0] || null,
    [groups, selectedDate, selectedPoint]);

  const groupIdx = useMemo(() => groups.findIndex(g => g.measured_at === selectedDate && g.point_name === (currentGroup?.point_name ?? '')), [groups, selectedDate, currentGroup]);
  const goPrev = () => { if (groupIdx > 0) { const g = groups[groupIdx - 1]; setSelectedDate(g.measured_at); setSelectedPoint(g.point_name); } };
  const goNext = () => { if (groupIdx < groups.length - 1) { const g = groups[groupIdx + 1]; setSelectedDate(g.measured_at); setSelectedPoint(g.point_name); } };

  // Close add menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target as Node)) setShowAddMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Panel management
  const addPanel = (tabType: 'twf' | 'fft', signal: Signal) => {
    const newPanel: Panel = { id: uid(), tabType, signal, heightPct: 0 };
    setPanels(prev => {
      const next = [...prev, newPanel];
      const equal = 100 / next.length;
      return next.map(p => ({ ...p, heightPct: equal }));
    });
    setActiveTab(tabType);
    setShowAddMenu(false);
  };
  const removePanel = (id: string) => {
    setPanels(prev => {
      const next = prev.filter(p => p.id !== id);
      const equal = 100 / Math.max(next.length, 1);
      return next.map(p => ({ ...p, heightPct: equal }));
    });
  };
  const updatePanelSignal = (id: string, signal: Signal) => {
    setPanels(prev => prev.map(p => p.id === id ? { ...p, signal } : p));
  };

  // Resize handle dragging
  const resizeDragRef = useRef<{ startY: number; panelIdx: number; initHeights: number[] } | null>(null);
  const containerRef  = useRef<HTMLDivElement>(null);

  const onResizeMouseDown = (e: React.MouseEvent, panelIdx: number) => {
    e.preventDefault();
    resizeDragRef.current = {
      startY: e.clientY,
      panelIdx,
      initHeights: panels.map(p => p.heightPct),
    };
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!resizeDragRef.current || !containerRef.current) return;
      const { startY, panelIdx, initHeights } = resizeDragRef.current;
      const containerH = containerRef.current.offsetHeight;
      const deltaPct = ((e.clientY - startY) / containerH) * 100;
      const minH = 15;
      const a = initHeights[panelIdx] + deltaPct;
      const b = initHeights[panelIdx + 1] - deltaPct;
      if (a < minH || b < minH) return;
      setPanels(prev => prev.map((p, i) => {
        if (i === panelIdx) return { ...p, heightPct: a };
        if (i === panelIdx + 1) return { ...p, heightPct: b };
        return p;
      }));
    };
    const onUp = () => { resizeDragRef.current = null; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
  }, [panels]);

  const fftPanels = panels.filter(p => p.tabType === 'fft');
  const twfPanels = panels.filter(p => p.tabType === 'twf');
  const activePanels = activeTab === 'fft' ? fftPanels : activeTab === 'twf' ? twfPanels : [];

  if (!state) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-500 mb-4">No analysis data found. Open from a machine page.</p>
          <a href="/" className="text-teal-600 hover:underline">← Back to dashboard</a>
        </div>
      </div>
    );
  }

  if (!getToken()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Session expired. Please log in again.</p>
          <a href="/" className="text-teal-600 hover:underline">← Go to login</a>
        </div>
      </div>
    );
  }

  const pointsForDate = groups.filter(g => g.measured_at === selectedDate);

  return (
    <div className="flex flex-col h-screen bg-white overflow-hidden">
      {/* Top bar */}
      <div className="flex items-center gap-3 px-4 py-2 bg-white border-b border-gray-200 shrink-0 z-10">
        <img src="/favicon.svg" alt="PC" className="w-7 h-7 shrink-0" />
        <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 shrink-0">
          <span>{machineName}</span>
          {currentGroup && <><span className="text-gray-300">·</span><span className="text-gray-500 font-normal">{currentGroup.point_name}</span></>}
        </div>

        {/* Date navigation */}
        <div className="flex items-center gap-1 ml-2">
          <button onClick={goPrev} disabled={groupIdx <= 0} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 text-gray-500"><ChevronLeft size={15} /></button>
          <span className="text-xs text-gray-500 font-mono whitespace-nowrap">{currentGroup ? fmtEastern(currentGroup.measured_at) : '…'}</span>
          <button onClick={goNext} disabled={groupIdx >= groups.length - 1} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 text-gray-500"><ChevronRight size={15} /></button>
          <span className="text-xs text-gray-400">({groupIdx + 1}/{groups.length})</span>
        </div>

        {/* Point selector */}
        {pointsForDate.length > 1 && (
          <div className="flex gap-1">
            {pointsForDate.map(g => (
              <button key={g.point_name} onClick={() => setSelectedPoint(g.point_name)}
                className={`text-xs px-2 py-0.5 rounded-full border ${g.point_name === selectedPoint ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
              >{g.point_name}</button>
            ))}
          </div>
        )}

        {/* Tab switcher */}
        <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs ml-auto">
          {(['twf','fft','waterfall'] as TabType[]).map((t, i) => (
            <button key={t} onClick={() => setActiveTab(t)}
              className={`px-3 py-1.5 ${i > 0 ? 'border-l border-gray-200' : ''} ${activeTab === t ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >{t.toUpperCase()}</button>
          ))}
        </div>

        {/* Add panel */}
        {activeTab !== 'waterfall' && (
          <div className="relative" ref={addMenuRef}>
            <button onClick={() => setShowAddMenu(v => !v)}
              className="flex items-center gap-1 text-xs px-2.5 py-1.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50"
            ><Plus size={13} /><span>Add Panel</span></button>
            {showAddMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl text-xs min-w-[160px] z-50 overflow-hidden">
                <div className="px-3 py-1.5 text-gray-400 font-semibold border-b border-gray-100">Add FFT</div>
                {SIGNALS.map(s => (
                  <button key={`fft-${s}`} onClick={() => addPanel('fft', s)} className="w-full text-left px-3 py-2 hover:bg-gray-50 text-gray-700">{signalLabel(s)} ({signalUnit(s)})</button>
                ))}
                <div className="px-3 py-1.5 text-gray-400 font-semibold border-t border-b border-gray-100">Add TWF</div>
                {(['accel','envelope'] as Signal[]).map(s => (
                  <button key={`twf-${s}`} onClick={() => addPanel('twf', s)} className="w-full text-left px-3 py-2 hover:bg-gray-50 text-gray-700">{signalLabel(s)} ({signalUnit(s)})</button>
                ))}
              </div>
            )}
          </div>
        )}

        <button onClick={() => window.close()} title="Close" className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700"><X size={16} /></button>
      </div>

      {/* Main content */}
      {activeTab === 'waterfall' ? (
        <WaterfallView machineId={machineId} currentGroup={currentGroup} operatingRpm={operatingRpm} />
      ) : (
        <div ref={containerRef} className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {activePanels.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
              <div className="text-center">
                <p>No {activeTab.toUpperCase()} panels.</p>
                <button onClick={() => addPanel(activeTab as 'twf' | 'fft', activeTab === 'fft' ? 'velocity' : 'accel')}
                  className="mt-2 text-teal-600 hover:underline text-xs">+ Add one</button>
              </div>
            </div>
          ) : (
            activePanels.map((panel, i) => {
              const PanelComp = panel.tabType === 'fft' ? FFTPanelView : TWFPanelView;
              return (
                <div key={panel.id} style={{ height: `${panel.heightPct}%` }} className="flex flex-col min-h-0">
                  <PanelComp
                    panelId={panel.id}
                    signal={panel.signal}
                    group={currentGroup}
                    operatingRpm={operatingRpm}
                    isVfd={isVfd}
                    canRemove={activePanels.length > 1}
                    onRemove={() => removePanel(panel.id)}
                    onSignalChange={sig => updatePanelSignal(panel.id, sig)}
                    style={{ flex: 1, minHeight: 0 }}
                  />
                  {/* Resize handle between panels */}
                  {i < activePanels.length - 1 && (
                    <div
                      className="h-2 bg-gray-100 hover:bg-teal-100 cursor-row-resize flex items-center justify-center shrink-0 group transition-colors"
                      onMouseDown={e => onResizeMouseDown(e, i)}
                      onDoubleClick={() => {
                        const equal = 100 / activePanels.length;
                        setPanels(prev => prev.map(p => activePanels.find(ap => ap.id === p.id) ? { ...p, heightPct: equal } : p));
                      }}
                      title="Drag to resize · Double-click to reset"
                    >
                      <GripVertical size={12} className="text-gray-300 group-hover:text-teal-400" />
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
